import { initializePaddle } from '@paddle/paddle-js';

import { paddleProductsError } from '../config/errors.config';
import {
  PADDLE_CLIENT_TOKEN,
  PADDLE_ENTERPRISE_PRODUCT,
  PADDLE_ENTERPRISE_PRODUCT_ANNUAL,
  PADDLE_ENTERPRISE_PRODUCT_ANNUAL_ADDITIONAL,
  PADDLE_ENTERPRISE_PRODUCT_MONTHLY,
  PADDLE_ENTERPRISE_PRODUCT_MONTHLY_ADDITIONAL,
  PADDLE_PERSONAL_PRODUCT,
  PADDLE_PERSONAL_PRODUCT_ANNUAL,
  PADDLE_PERSONAL_PRODUCT_MONTHLY,
  PADDLE_SANDBOX,
  PADDLE_TEAM_PRODUCT,
  PADDLE_TEAM_PRODUCT_ANNUAL,
  PADDLE_TEAM_PRODUCT_ANNUAL_ADDITIONAL,
  PADDLE_TEAM_PRODUCT_MONTHLY,
  PADDLE_TEAM_PRODUCT_MONTHLY_ADDITIONAL
} from '../constants/secrets';
import { PaddleProducts, Plan } from '../types/pricing.types';
import { CurrencyMap } from '../util/currency';
import { ServiceError } from './errors';

export const getPaddleProductPrices = async (): Promise<
  PaddleProducts | undefined
> => {
  const paddleInstance = await initializePaddle({
    environment: PADDLE_SANDBOX ? 'sandbox' : 'production',
    token: PADDLE_CLIENT_TOKEN
  });

  const prices = await paddleInstance?.PricePreview({
    items: [
      {
        priceId: PADDLE_PERSONAL_PRODUCT_MONTHLY,
        quantity: 1
      },
      {
        priceId: PADDLE_PERSONAL_PRODUCT_ANNUAL,
        quantity: 1
      },
      {
        priceId: PADDLE_TEAM_PRODUCT_MONTHLY,
        quantity: 1
      },
      {
        priceId: PADDLE_TEAM_PRODUCT_MONTHLY_ADDITIONAL,
        quantity: 1
      },
      {
        priceId: PADDLE_TEAM_PRODUCT_ANNUAL,
        quantity: 1
      },
      {
        priceId: PADDLE_TEAM_PRODUCT_ANNUAL_ADDITIONAL,
        quantity: 1
      },
      {
        priceId: PADDLE_ENTERPRISE_PRODUCT_MONTHLY,
        quantity: 1
      },
      {
        priceId: PADDLE_ENTERPRISE_PRODUCT_MONTHLY_ADDITIONAL,
        quantity: 1
      },
      {
        priceId: PADDLE_ENTERPRISE_PRODUCT_ANNUAL,
        quantity: 1
      },
      {
        priceId: PADDLE_ENTERPRISE_PRODUCT_ANNUAL_ADDITIONAL,
        quantity: 1
      }
    ]
  });

  const cleanedPrices =
    prices?.data.details.lineItems.map((items) => ({
      id: items.price.id,
      productId: items.product.id,
      name: items.price.name ?? '',
      description: items.price.description ?? '',
      type: items.price.customData?.type,
      annual: items.price.customData?.billing === 'annual',
      additional: Boolean(items.price.customData?.additional),
      price: parseInt(items.price.unitPrice.amount) / 100,
      currencyCode: items.price.unitPrice.currencyCode,
      currency: CurrencyMap.USD
    })) ?? [];

  return prices?.data.details.lineItems.reduce((acc, curr) => {
    const productId = curr.product.id;
    if (productId === PADDLE_PERSONAL_PRODUCT) {
      const monthlyPrice = cleanedPrices.find(
        (price) => price.productId === productId && !price.annual
      );
      const annualPrice = cleanedPrices.find(
        (price) => price.productId === productId && price.annual
      );

      if (!monthlyPrice || !annualPrice) {
        throw new ServiceError(
          paddleProductsError.title,
          paddleProductsError.message
        );
      }

      acc[Plan.Personal] = {
        id: productId,
        name: curr.product.name,
        description: curr.product.description ?? '',
        prices: {
          monthly: monthlyPrice,
          annual: annualPrice
        }
      };
    } else if (productId === PADDLE_TEAM_PRODUCT) {
      const monthlyPrice = cleanedPrices.find(
        (price) =>
          price.productId === productId && !price.annual && !price.additional
      );
      const monthlyAdditionalPrice = cleanedPrices.find(
        (price) =>
          price.productId === productId && !price.annual && price.additional
      );
      const annualPrice = cleanedPrices.find(
        (price) =>
          price.productId === productId && price.annual && !price.additional
      );
      const annualAdditionalPrice = cleanedPrices.find(
        (price) =>
          price.productId === productId && price.annual && price.additional
      );

      if (
        !monthlyPrice ||
        !monthlyAdditionalPrice ||
        !annualPrice ||
        !annualAdditionalPrice
      ) {
        throw new ServiceError(
          paddleProductsError.title,
          paddleProductsError.message
        );
      }

      acc[Plan.Team] = {
        id: productId,
        name: curr.product.name,
        description: curr.product.description ?? '',
        prices: {
          monthly: monthlyPrice,
          monthlyAdditional: monthlyAdditionalPrice,
          annual: annualPrice,
          annualAdditional: annualAdditionalPrice
        }
      };
    } else if (productId === PADDLE_ENTERPRISE_PRODUCT) {
      const monthlyPrice = cleanedPrices.find(
        (price) =>
          price.productId === productId && !price.annual && !price.additional
      );
      const monthlyAdditionalPrice = cleanedPrices.find(
        (price) =>
          price.productId === productId && !price.annual && price.additional
      );
      const annualPrice = cleanedPrices.find(
        (price) =>
          price.productId === productId && price.annual && !price.additional
      );
      const annualAdditionalPrice = cleanedPrices.find(
        (price) =>
          price.productId === productId && price.annual && price.additional
      );

      if (
        !monthlyPrice ||
        !monthlyAdditionalPrice ||
        !annualPrice ||
        !annualAdditionalPrice
      ) {
        throw new ServiceError(
          paddleProductsError.title,
          paddleProductsError.message
        );
      }

      acc[Plan.Enterprise] = {
        id: productId,
        name: curr.product.name,
        description: curr.product.description ?? '',
        prices: {
          monthly: monthlyPrice,
          monthlyAdditional: monthlyAdditionalPrice,
          annual: annualPrice,
          annualAdditional: annualAdditionalPrice
        }
      };
    }

    return acc;
  }, {} as PaddleProducts);
};
